/**
 * AuthGate - Protected page wrapper with blurred preview
 * 
 * Shows a tempting blurred preview of page content with a centered login modal
 * for unauthenticated users. Once authenticated, renders children directly.
 */

import { useState, type ReactNode } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { Button } from "@/components/ui/button";

type AuthGateProps = {
  children: ReactNode;
  title: string;
  description: string;
};

export const AuthGate = ({ children, title, description }: AuthGateProps) => {
  const { isAuthenticated } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // User is authenticated - show real content
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // User is not authenticated - show blurred preview with login overlay
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
      {/* Blurred Background Preview - MORE VISIBLE */}
      <div className="absolute inset-0 pointer-events-none select-none">
        {/* Actual page content - less blur, higher opacity */}
        <div className="blur-[3px] opacity-60 animate-pulse">
          {children}
        </div>
        
        {/* Lighter gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/45 to-background/65" />
      </div>

      {/* Centered Login Prompt (Modal Style) */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-4rem)] p-8">
        <div className="text-center max-w-md w-full">
          {/* Glassmorphic Card Container */}
          <div className="bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl">
            {/* Title */}
            <h2 className="text-2xl font-bold mb-3">{title}</h2>

            {/* Description */}
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {description}
            </p>

            {/* CTA Button - CORRECT THEME GREEN */}
            <Button 
              onClick={() => setLoginModalOpen(true)} 
              size="lg"
              className="w-full text-lg py-6 bg-[#2ebd85] hover:bg-[#2ebd85]/90 text-white border-0"
            >
              Connect to Continue
            </Button>
          </div>
        </div>
      </div>

      <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
    </div>
  );
};

